import { useEffect, useRef, useState } from "react";
import cls from "classnames";
import { isIOS as isIOSDetect } from "react-device-detect";
import lottie from "lottie-web";

import useSessionStore from "../../../store/sessionStore";
import confetti from "../../../../public/assets/lottie-animations/lottie-confetti.json";
import {
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
  WhatsappIcon,
} from "../../SocialIcons";
import * as Track from "@qoohoo/common/utils/analytics/track";
import { pageview, link, button } from "@qoohoo/common/constants/analytics";
import { appStoreUrl, playStoreUrl } from "../../../constants/constants";
import IOSDeepLink from "../../IOSDeepLink";
import { sendShareEventViaPixel } from "@qoohoo/common/utils/analytics/pixel";
import { addTagsInRecording } from "@qoohoo/common/utils/analytics/clarity";
import {
  getWhatsappShareUrl,
  prepareShareObject,
} from "@qoohoo/common/utils/social-share";
import {openInNewTab} from "@qoohoo/common/utils/url";

const ThankyouModal = ({ channelDetails, isPrivate, customSettings }) => {
  const userData = useSessionStore((state) => state.userData);

  const [isIOS, setIsIOS] = useState(false);

  const container = useRef(null);

  const deepLink = channelDetails?.short_url_flutter;
  const shareMessage = `I invite you to join channel ${channelDetails?.name} on qoohoo!`;

  const isCourse = channelDetails?.type?.toLowerCase() === "course";
  const isVisbleOnWeb = channelDetails?.visible_on_web;

  let modalText = customSettings && customSettings?.thankyou_modal_settings?.message || 'Please join me on my channel using the link sent on your phone number.';

  if (isCourse) {
    if (isVisbleOnWeb) {
      modalText =
        "You can access the course by clicking on “Go to course” button.";
    } else {
      modalText = "Please download the app to checkout the course lessons.";
    }
  }

  const shareObject = prepareShareObject(deepLink, shareMessage);
  const shortEncodedUrl = shareObject.encodedUrl;
  const nonUrlShareableEncodedUrl = shareObject.encodedText;
  const whatsappShareUrl = getWhatsappShareUrl({
    text: shareObject.encodedTextWithUrl,
  });

  const isPaid = channelDetails.is_paid;
  const current_screen = pageview.channel_details;
  const modal_name = "thank_you_modal";
  const trackDetails = {
    current_screen,
    modal_name,
    is_paid: isPaid,
    uid: channelDetails.uid,
    name: channelDetails.name,
    type: channelDetails.type,
    recurring_type: channelDetails?.plans?.[0]?.plan_type,
    "creator.uid": channelDetails.creator.uid,
    "creator.full_name": channelDetails.creator.fullname,
    "creator.username": channelDetails.creator.username,
  };

  function sendSharePixelEventOnClick(medium) {
    sendShareEventViaPixel({
      ...trackDetails,
      share_via: medium,
    });
  }

  useEffect(() => {
    lottie.loadAnimation({
      container: container.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: confetti,
    });

    addTagsInRecording({ thank_you_modal: true, ...trackDetails });

    Track.customEvent(modal_name, {
      actionType: "modal_open",
      label: modal_name,
      ...trackDetails,
    });

    if (isIOSDetect) {
      setIsIOS(true);
    }
    if (customSettings?.redirection_on_success) {
      openInNewTab(customSettings.redirection_on_success, 1250);
    }
  }, []);

  const onPaymentSuccessRedirection = customSettings?.redirection_on_success && (
    <p className="my-6 text-white text-base font-bold">
      Redirecting...
    </p>
  );

  let hasJoinButton = true;

  if (typeof customSettings?.thankyou_modal_settings?.has_join_btn !== 'undefined') {
    hasJoinButton = customSettings?.thankyou_modal_settings?.has_join_btn;
  }

  return (
    <>
      <div className="absolute left-0 right-0 mx-auto sm:-top-40 -top-48 z-[1] pointer-events-none">
        <div className="container pointer-events-none" ref={container}></div>
      </div>
      <div className={cls(
        "leading-8 font-bold",
        customSettings?.thankyou_modal_settings?.heading ? 'text-2xl' : 'text-4xl '
      )}>
        {customSettings && customSettings?.thankyou_modal_settings?.heading || 'Thank you...'}
      </div>
      {onPaymentSuccessRedirection}
      <p className="text-md text-[#F0F0F0] mt-4">{modalText}</p>
      {isCourse && !isVisbleOnWeb && (
        <div className="flex-row items-center justify-between hidden mt-8 sm:flex">
          <a
            href={playStoreUrl}
            onClick={() => {
              addTagsInRecording({
                download_app: true,
                platform: "android",
              });

              Track.buttonClick(button.download_app, {
                url: playStoreUrl,
                platform_target: "android",
                current_screen,
                modal_name,
              });
            }}
          >
            <img
              src="/assets/icons/google_play.svg"
              alt="Play store image"
              className="w-[154px]"
            />
          </a>
          <a
            href={appStoreUrl}
            onClick={() => {
              addTagsInRecording({
                download_app: true,
                platform: "ios",
              });

              Track.buttonClick(button.download_app, {
                platform_target: "ios",
                url: appStoreUrl,
                current_screen,
                modal_name,
              });
            }}
          >
            <img
              src="/assets/icons/app-store-badge.svg"
              alt="App store image"
              className="w-[137]"
            />
          </a>
        </div>
      )}
      {
        hasJoinButton && (
          <div className="mx-auto z-20 mt-6 sm:hidden hover:cursor-pointer">
            {isIOS ? (
              <IOSDeepLink
                deepLink={channelDetails.short_url_flutter}
                onClick={() => {
                  Track.linkClick(link.open_in_app, {
                    url: channelDetails.short_url_flutter,
                    current_screen,
                    modal_name,
                  });
                }}
              />
            ) : (
              <a
                href={`${channelDetails.short_url_flutter}?intent=app`}
                className="underline"
                onClick={() => {
                  addTagsInRecording({ open_in_app: true, platform: "android" });

                  Track.linkClick(link.open_in_app, {
                    url: channelDetails.short_url_flutter + "?intent=app",
                    ...trackDetails,
                  });
                }}
              >
                Open in App
              </a>
            )}
          </div>
        )
      }
      {!isPrivate && (
        <div className="mt-16 flex flex-col bg-[#2C343A] py-6 -mx-6 -my-10 sm:rounded-b-2xl">
          <div className="max-w-[184px] mx-auto">
            <p className="text-link font-semibold text-center">{`Invite your friends \u270C`}</p>
            <div className="flex flex-row items-center mt-5 space-x-6">
              <a
                href={whatsappShareUrl}
                target="_blank"
                className="fill-[#E8E8E8] hover:fill-[#25D366] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "whatsapp",
                  });

                  sendSharePixelEventOnClick("whatsapp");

                  Track.linkClick(link.share_via_whatsapp, {
                    url: whatsappShareUrl,
                    ...trackDetails,
                  });
                }}
              >
                <WhatsappIcon />
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${shortEncodedUrl}&quote=${nonUrlShareableEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] hover:fill-[#1877F2] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "facebook",
                  });

                  sendSharePixelEventOnClick("facebook");

                  Track.linkClick(link.share_via_facebook, {
                    url: `https://www.facebook.com/sharer/sharer.php?u=${shortEncodedUrl}&quote=${nonUrlShareableEncodedUrl}`,
                    ...trackDetails,
                  });
                }}
              >
                <FacebookIcon />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${nonUrlShareableEncodedUrl}&url=${shortEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] hover:fill-[#1DA1F2] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "twitter",
                  });

                  sendSharePixelEventOnClick("twitter");

                  Track.linkClick(link.share_via_twitter, {
                    url: `https://twitter.com/intent/tweet?text=${nonUrlShareableEncodedUrl}&url=${shortEncodedUrl}`,
                    ...trackDetails,
                  });
                }}
              >
                <TwitterIcon />
              </a>
              <a
                // TODO: Add linkedin api for sharing
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${shortEncodedUrl}&title=${nonUrlShareableEncodedUrl}`}
                target="_blank"
                className="fill-[#E8E8E8] hover:fill-[#00A0DC] outline-none"
                onClick={() => {
                  addTagsInRecording({
                    share: "linkedin",
                  });

                  sendSharePixelEventOnClick("linkedin");

                  Track.linkClick(link.share_via_linkedin, {
                    url: `https://www.linkedin.com/shareArticle?mini=true&url=${shortEncodedUrl}&title=${nonUrlShareableEncodedUrl}`,
                    ...trackDetails,
                  });
                }}
              >
                <LinkedinIcon />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ThankyouModal;
